/**
 * propertyKpis.js — SOURCE UNIQUE des KPI de tête d'un bien (2 à 4 selon l'opération).
 *
 * Consommé par :
 *   - la fiche consultation interne (`PropertyViewContent.jsx`)
 *   - la fiche publique partageable (`PropertyPublicPage.jsx`)
 *   - miroir Python pour le PDF (`backend/pdf_generator.py::_fb_kpi_data`)
 *
 * Règles :
 *   - Même ordre, mêmes libellés, mêmes icônes sur toutes les surfaces.
 *   - Liste des KPI **arbitrée par le client** par opération (cf. buildPropertyKpis).
 *   - Aucune donnée confidentielle (CA, EBE, net vendeur) dans les KPI. SEULE exception
 *     validée client : le montant forfaitaire TTC des honoraires sur une **licence à vendre**.
 *   - La fiche publique NE recalcule rien : elle consomme ce module tel quel.
 */

import { isLicenceAsset, isPartsSocialesAsset } from '../rules/propertyEditTabs';
import { getActiviteMetier, getVille, getSecteur } from './propertyDataMapper';
import { chargeLabel, deriveHonorairesView, prixAffichePublic, libellePrixTotal, suffixePrixTotal } from './honorairesModel';
import { formatSurfaceFR } from './formatFR';

export const OPERATION_LABELS = {
  cession: 'Cession (PME/PMI & DAB)',
  vente_immo: 'Vente immobilière (murs)',
  vente_murs: 'Vente immobilière (murs)',
  vente_fonds: 'Cession (PME/PMI & DAB)',
  cession_bail: 'Cession de bail',
  location: 'Location pure',
  location_droit_entree: 'Location + pas-de-porte',
  location_gerance: 'Location-gérance',
  parts_sociales: 'Cession de parts sociales',
  licence: 'Licence de débit de boissons',
};

export const PROFIL_LABELS = {
  chr: 'CHR / hôtellerie-restauration', retail: 'Commerce de détail', commerce: 'Commerce', boutique: 'Commerce',
  bureaux: 'Bureaux', tertiaire: 'Tertiaire',
  activite_logistique: 'Activité / Logistique', industriel: 'Industriel', logistique: 'Logistique',
  services: 'Services non réglementés', services_reglementes: 'Services réglementés',
  production_reparation: 'Production / réparation / artisanat',
  services_liberaux: 'Services & professions libérales', investissement: 'Investissement',
  licence: 'Licence',
};

const LICENCE_LABELS = {
  licence_iv: 'Licence IV',
  licence_iii: 'Licence III',
  licence_restaurant: 'Licence Restaurant',
  licence_petite: 'Petite licence',
  licence_autre: 'Autre licence',
};

const isCessionFamily = (op) => ['cession', 'vente_fonds', 'cession_bail'].includes(op);
const isVenteImmoFamily = (op) => ['vente_immo', 'vente_murs'].includes(op);

const fmtEuro = (n) => {
  if (n === null || n === undefined || n === '') return null;
  const v = Number(n);
  if (Number.isNaN(v)) return null;
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v);
};

const cap = (s) => (s ? String(s).charAt(0).toUpperCase() + String(s).slice(1) : '');

export const formatLicenceLabel = (s) => (s ? (LICENCE_LABELS[s] || s) : null);

/** Taxonomie unifiée (objet imbriqué OU champs racine). */
export const getTaxonomyCtx = (property) => {
  const p = property || {};
  const t = (p.taxonomy && typeof p.taxonomy === 'object') ? p.taxonomy : {};
  return {
    operation: t.operation || p.operation,
    objet: t.objet || p.objet,
    profil: t.profil || p.profil,
    sous_profil: t.sous_profil || p.sous_profil,
    cible: t.cible || p.cible,
  };
};

/** Libellé métier affiché en KPI « Activité » et en chip hero (source unique). */
export const getMetierLabel = (property) => {
  const p = property || {};
  const bg = p.bien_general || {};
  const tax = getTaxonomyCtx(p);
  const activite = getActiviteMetier(p);
  if (activite && activite !== 'N/C') return activite;
  const raw = tax.profil || bg.profil;
  return PROFIL_LABELS[String(raw || '').toLowerCase()] || cap(raw);
};

/**
 * Honoraires — QUI PAIE (jamais le montant).
 * Source de vérité : location → informations_bail.honoraires_charge,
 * vente / cession → informations_vente.honoraires_charge.
 * Cf. /app/memory/HONORAIRES-source-de-verite.md
 */
export const getHonorairesCharge = (property) => {
  const p = property || {};
  const operation = getTaxonomyCtx(p).operation;
  const isLoc = ['location', 'location_droit_entree', 'location_gerance'].includes(operation);
  const bail = p.informations_bail || {};
  const vente = p.informations_vente || {};
  const raw = isLoc ? (bail.honoraires_charge || vente.honoraires_charge) : (vente.honoraires_charge || bail.honoraires_charge);
  if (!raw) return null;
  return { raw: String(raw), isLoc };
};

/** « Honoraires à la charge du preneur » — mention publique, sans montant. Null si non renseigné. */
export const formatHonorairesChargeMention = (property) => {
  const h = getHonorairesCharge(property);
  if (!h) return null;
  const v = h.raw.toLowerCase();
  const who = h.isLoc
    ? (v === 'acquereur' || v === 'preneur' ? 'du preneur' : v === 'vendeur' || v === 'bailleur' ? 'du bailleur' : `de : ${h.raw}`)
    : (v === 'acquereur' ? "de l'acquéreur" : v === 'vendeur' ? 'du vendeur' : `de : ${h.raw}`);
  return `Honoraires à la charge ${who}`;
};

/** Libellés du type d'emplacement — miroir de `rules/coreFieldsRules.json` (enumLabels). */
const EMPLACEMENT_LABELS = {
  n1: 'N°1 (meilleur emplacement)',
  n1bis: 'N°1 bis (très bon emplacement)',
  n2: 'N°2 (bon emplacement)',
  hyper_centre: 'Hyper-centre',
  centre_ville: 'Centre-ville',
  quartier_affaires: "Quartier d'affaires",
  axe_passant: 'Axe passant',
  angle_rue: 'Angle de rue',
  avenue_principale: 'Avenue principale',
  rue_pietonne: 'Rue piétonne',
  galerie_cc: 'Galerie de centre commercial',
  zone_commerciale: 'Zone commerciale',
  zone_activite: "Zone d'activité",
  peripherie: 'Périphérie',
  parking_proximite: 'Parking à proximité',
};

/** Type d'emplacement commercial (KPI dédié depuis iter128). */
export const getEmplacementLabel = (property) => {
  const code = property?.public_location?.emplacement_code
    || property?.bien_general?.emplacement
    || null;
  if (!code) return null;
  return EMPLACEMENT_LABELS[code] || String(code);
};

/** Ville + secteur en UN SEUL KPI (« Nice — centre-ville »). */
export const getVilleLocalisationLabel = (property) => {
  const ville = getVille(property);
  const secteur = getSecteur(property);
  const v = ville && ville !== 'Ville NC' ? ville : null;
  const s = secteur && secteur !== 'Secteur NC' ? secteur : null;
  if (v && s) return `${v} — ${s}`;
  return v || s || null;
};

/** Licence de débit de boissons vendue/louée seule (actif incorporel). */
const isLicenceOperation = (tax) => (
  tax.operation === 'licence'
  || ['licence', 'licence_exploitation'].includes(String(tax.objet || '').toLowerCase())
  || isLicenceAsset(tax)
);

/** Murs occupés (loués) vs libres — distingue les deux jeux de KPI de la vente de murs. */
const isMursOccupes = (p, vente) => (
  (p.bien_general || {}).occupe === true
  || String(vente.occupation_murs || '').toLowerCase() === 'occupe'
);

/**
 * Construit les KPI de tête (2 à 4 selon l'opération).
 *
 * ⚠️ LISTE ARBITRÉE PAR LE CLIENT (iter122) — ne rien ajouter, ne rien retirer :
 *   Licence à louer ......... Ville · Emplacement · À louer  · Redevance · Dépôt de garantie
 *   Licence à vendre ........ Ville · Emplacement · À vendre · Prix · Honoraires (montant fixe TTC)
 *   Murs occupés ............ Ville · Emplacement · Rentabilité · Superficie · Prix
 *   Murs libres ............. Ville · Emplacement · Superficie · Prix
 *   Droit au bail ........... Ville · Emplacement · Superficie · Prix
 *   Location droit d'entrée . Ville · Emplacement · Loyer · Droit d'entrée
 *   Fonds de commerce ....... Ville · Emplacement · Activité · Prix
 *   Location pure ........... Ville · Emplacement · Loyer
 * Les opérations non listées (parts sociales, location-gérance d'un fonds, immeuble,
 * terrain…) conservent leurs KPI historiques — à définir avec le client.
 *
 * La rangée s'adapte au nombre réel de KPI : un KPI sans valeur est retiré (jamais
 * de case vide), sauf pour les branches historiques qui affichent « — ».
 *
 * @returns {Array<{key:string,icon:string,iconClass:string,label:string,value:?string,sub:?string}>}
 */
export function buildPropertyKpis(property, { publicView = false } = {}) {
  const p = property || {};
  const bg = p.bien_general || {};
  const comp = p.composition || {};
  const vente = p.informations_vente || {};
  const bail = p.informations_bail || {};
  const tax = getTaxonomyCtx(p);
  const operation = tax.operation;
  const objet = String(tax.objet || '').toLowerCase();
  const ops = OPERATION_LABELS[operation] || '—';
  const surface = formatSurfaceFR(comp.superficie_totale, { fallback: null });
  const surfaceSub = comp.terrasse_m2 ? `+ terrasse ${comp.terrasse_m2} m²` : null;
  const metier = getMetierLabel(p);
  const kpiActivite = { key: 'activite', icon: 'bi-shop', iconClass: 'text-blue-600', label: 'Activité', value: metier || '—', sub: ops };
  const kpiSurface = { key: 'surface', icon: 'bi-arrows-angle-expand', iconClass: 'text-amber-600', label: 'Surface totale', value: surface, sub: surfaceSub };

  // KPI communs à toutes les opérations de la liste client — iter128 : la ville et le
  // type d'emplacement sont DEUX KPI distincts (le secteur n'est plus affiché en KPI).
  // Valeur « — » volontaire quand la donnée manque : le KPI reste visible pour inciter
  // le négociateur à compléter la fiche.
  const villeLabel = getVille(p);
  const kpiVille = {
    key: 'ville', icon: 'bi-geo-alt', iconClass: 'text-blue-600',
    label: 'Ville',
    value: (villeLabel && villeLabel !== 'Ville NC') ? villeLabel : '—',
  };
  const kpiEmplacement = {
    key: 'emplacement', icon: 'bi-signpost-2', iconClass: 'text-blue-600',
    label: "Type d'emplacement", value: getEmplacementLabel(p) || '—',
  };
  // iter130 — une licence de débit de boissons vendue/louée seule n'a PAS de local :
  // le type d'emplacement est hors sujet, on ne l'affiche pas.
  const kpiLocalisation = isLicenceAsset(tax) ? [kpiVille] : [kpiVille, kpiEmplacement];
  const kpiSuperficie = { key: 'surface', icon: 'bi-arrows-angle-expand', iconClass: 'text-amber-600', label: 'Superficie', value: surface, sub: surfaceSub };
  const clean = (list) => list.filter((k) => k.value != null && k.value !== '');

  /**
   * KPI prix — iter127 :
   *   charge acquéreur            → le prix du bien (net vendeur) SEUL en public
   *                                 (la décomposition vit dans son bloc dédié) ;
   *                                 en interne, la commission reste en sous-ligne pédagogique
   *   charge vendeur / non saisie → le prix FAI (tout compris)
   */
  const kpiPrix = () => {
    const hv = deriveHonorairesView(p);
    const px = prixAffichePublic(hv);
    if (px) {
      return {
        key: 'prix', icon: 'bi-currency-euro', iconClass: 'text-emerald-600',
        label: px.isFai ? libellePrixTotal(hv.charge) : 'Prix du bien',
        value: fmtEuro(px.value),
        sub: (publicView || px.isFai || hv.ttc == null)
          ? null
          : `+ ${fmtEuro(hv.ttc)} d'honoraires TTC — soit ${fmtEuro(hv.fai)} ${suffixePrixTotal(hv.charge)}`,
      };
    }
    return {
      key: 'prix', icon: 'bi-currency-euro', iconClass: 'text-emerald-600', label: 'Prix',
      value: fmtEuro(vente.prix_cession || vente.net_vendeur),
    };
  };

  // PRIORITÉ 1 — Licence de débit de boissons (louée ou vendue seule)
  if (isLicenceOperation(tax)) {
    const aLouer = ['location', 'location_gerance', 'location_droit_entree'].includes(operation);
    if (aLouer) {
      return clean([
        ...kpiLocalisation,
        { key: 'etat', icon: 'bi-tag', iconClass: 'text-slate-600', label: 'État', value: 'À louer' },
        { key: 'redevance', icon: 'bi-cash-coin', iconClass: 'text-emerald-600', label: 'Redevance /mois', value: fmtEuro(bail.redevance_mensuelle || bail.loyer_mensuel) },
        { key: 'depot_garantie', icon: 'bi-shield-check', iconClass: 'text-blue-600', label: 'Dépôt de garantie', value: fmtEuro(bail.depot_garantie) },
      ]);
    }
    // Licence à vendre — EXCEPTION arbitrée par le client : le montant d'honoraires
    // (forfait TTC, connu d'avance) est affiché y compris en public, quelle que soit
    // la charge. Aucun taux, aucune décomposition : uniquement le montant fixe TTC.
    // ⚠️ Cette exception ne concerne QUE ce KPI de licence — le bloc détaillé des
    // honoraires en bas de fiche suit la règle générale (cf. honorairesModel.js).
    const honoTtc = vente.honoraires_ttc ?? vente.honoraires_fixe;
    const chargeMention = chargeLabel(vente.honoraires_charge, false);
    return clean([
      ...kpiLocalisation,
      { key: 'etat', icon: 'bi-tag', iconClass: 'text-slate-600', label: 'État', value: 'À vendre' },
      kpiPrix(),
      {
        key: 'honoraires', icon: 'bi-percent', iconClass: 'text-amber-600', label: 'Honoraires',
        value: fmtEuro(honoTtc),
        sub: chargeMention ? `à la charge : ${String(chargeMention).toLowerCase()}` : null,
      },
    ]);
  }

  // PRIORITÉ 2 — Vente de murs : occupés (investisseur) vs libres
  if (isVenteImmoFamily(operation)) {
    const rentabilite = vente.taux_rendement_brut != null ? `${vente.taux_rendement_brut} %` : null;
    return clean([
      ...kpiLocalisation,
      ...(isMursOccupes(p, vente)
        ? [{ key: 'rentabilite', icon: 'bi-percent', iconClass: 'text-blue-600', label: 'Rentabilité', value: rentabilite }]
        : []),
      kpiSuperficie,
      kpiPrix(),
    ]);
  }

  // PRIORITÉ 3 — Droit au bail
  if (isCessionFamily(operation) && objet === 'droit_au_bail') {
    return clean([...kpiLocalisation, kpiSuperficie, kpiPrix()]);
  }

  // PRIORITÉ 4 — Fonds de commerce
  if (isCessionFamily(operation) && ['fonds_de_commerce', 'fonds_commerce'].includes(objet)) {
    return clean([
      ...kpiLocalisation,
      { key: 'activite', icon: 'bi-shop', iconClass: 'text-blue-600', label: 'Activité', value: metier },
      kpiPrix(),
    ]);
  }

  // PRIORITÉ 5 — Location avec droit d'entrée
  if (operation === 'location_droit_entree') {
    return clean([
      ...kpiLocalisation,
      { key: 'loyer', icon: 'bi-cash-coin', iconClass: 'text-emerald-600', label: 'Loyer /mois', value: fmtEuro(bail.loyer_mensuel), sub: 'HT' },
      { key: 'droit_entree', icon: 'bi-key-fill', iconClass: 'text-slate-600', label: "Droit d'entrée", value: fmtEuro(bail.droit_entree) },
    ]);
  }

  // PRIORITÉ 6 — Location pure
  if (operation === 'location') {
    return clean([
      ...kpiLocalisation,
      { key: 'loyer', icon: 'bi-cash-coin', iconClass: 'text-emerald-600', label: 'Loyer /mois', value: fmtEuro(bail.loyer_mensuel), sub: 'HT' },
    ]);
  }

  // ─── Opérations hors liste client : KPI historiques conservés ───
  // Cession de parts sociales (actif incorporel)
  if (operation === 'parts_sociales' || isPartsSocialesAsset(tax)) {
    const pct = vente.pourcentage_capital_cede ?? vente.pourcentage_parts;
    return [
      { key: 'prix', icon: 'bi-bank', iconClass: 'text-emerald-600', label: 'Prix des parts', value: fmtEuro(vente.prix_cession || vente.net_vendeur) },
      { key: 'capital', icon: 'bi-cash-stack', iconClass: 'text-blue-600', label: 'Capital social', value: fmtEuro(vente.capital_social) },
      { key: 'pct', icon: 'bi-percent', iconClass: 'text-slate-600', label: '% des parts', value: pct != null ? `${pct}%` : null },
      kpiActivite,
    ].filter((k) => k.value != null);
  }

  // Cession d'autres objets (murs, immeuble, terrain…) — KPI historiques
  if (isCessionFamily(operation)) {
    const noRentObjets = ['murs_commerciaux', 'terrain', 'licence', 'licence_exploitation', 'parts_sociales', 'immeuble'];
    const skipRent = noRentObjets.includes(tax.objet);
    return [
      { key: 'prix', icon: 'bi-currency-euro', iconClass: 'text-emerald-600', label: 'Prix de cession', value: fmtEuro(vente.prix_cession || vente.net_vendeur) },
      ...(!skipRent ? [{
        key: 'loyer', icon: 'bi-receipt', iconClass: 'text-slate-600', label: 'Loyer mensuel',
        value: bail.loyer_mensuel != null ? fmtEuro(bail.loyer_mensuel) : '—',
        sub: bail.loyer_mensuel != null ? 'HT — Charges non incluses' : 'N/C',
      }] : []),
      kpiSurface,
      kpiActivite,
    ];
  }

  // Location-gérance d'un fonds (contrat distinct de la location de licence)
  if (operation === 'location_gerance') {
    return [
      { key: 'prix', icon: 'bi-cash-coin', iconClass: 'text-emerald-600', label: 'Redevance /mois', value: fmtEuro(bail.redevance_mensuelle) },
      { key: 'duree', icon: 'bi-calendar-range', iconClass: 'text-blue-600', label: 'Durée gérance', value: bail.duree_gerance || '—' },
      kpiSurface,
      kpiActivite,
    ];
  }

  // Fallback
  return [
    { key: 'prix', icon: 'bi-currency-euro', iconClass: 'text-emerald-600', label: 'Prix', value: fmtEuro(vente.prix_cession || vente.net_vendeur) },
    kpiSurface,
    { key: 'ville', icon: 'bi-geo-alt', iconClass: 'text-blue-600', label: 'Ville', value: (p.public_location || {}).city_label || bg.ville_generale || '—' },
    { key: 'type', icon: 'bi-shop', iconClass: 'text-slate-600', label: 'Type', value: ops },
  ];
}

export const KPI_GRID_CLASS = {
  1: 'grid-cols-1',
  2: 'grid-cols-2 md:grid-cols-2',
  3: 'grid-cols-2 md:grid-cols-3',
  4: 'grid-cols-2 md:grid-cols-4',
  5: 'grid-cols-2 md:grid-cols-3 lg:grid-cols-5',
};

export default buildPropertyKpis;
